import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import Grid from "../../components/Grid/Grid";
import { CUSTOM_MESSAGES, PAGE_SIZES } from "../../constants/globalConstants";
import { Layout } from "../../layout/PortalLayout/PortalLayout";
import MainHeading from "../../components/MainHeading/MainHeading";
import CustomButton from "../../components/CustomButton/CustomButton";
import ThreeDotActionItem from "../../components/ThreeDotActionItem/ThreeDotActionItem";
import useGridHeight from "../../hooks/useGridHeight";
import { useLazyGetModelListQuery } from "../../app/api/modelApi";
import { useLazyGetFamilyListQuery } from "../../app/api/familyApi";
import { useLazyGetBrandListQuery } from "../../app/api/brandApi";
import { useLazyGetFileListQuery } from "../../app/api/fileApi";
import { useLazyGetTemplateListQuery } from "../../app/api/templateApi";
import { handleApiResponse } from "../../utils/handleApiResponse";
import { useFilterManager } from "../../hooks/useFilterManager";
import { useAppSelector } from "../../redux/store";

const FilePortal = () => {
  const { handleFilterChange } = useFilterManager();
  const persistDropdownSelector = useAppSelector(
    (state) => state.dropdownSelector
  );
  const [tableData, setTableData] = useState<any[]>([]);
  const [brandId, setBrandId] = useState<string | null>(
    persistDropdownSelector["brand_selector"]?.value ?? null
  );
  const [familyId, setFamilyId] = useState<string | null>(
    persistDropdownSelector["family_selector"]?.value ?? null
  );
  const [modelId, setModelId] = useState<string | null>(
    persistDropdownSelector["model_selector"]?.value ?? null
  );
  const [templateId, setTemplateId] = useState<string | null>(
    persistDropdownSelector["template_selector"]?.value ?? null
  );

  const navigate = useNavigate();
  useGridHeight();
  const [getBrandListQuery] = useLazyGetBrandListQuery();
  const [getFamilyListQuery] = useLazyGetFamilyListQuery();
  const [getModelListQuery] = useLazyGetModelListQuery();
  const [getTemplateListQuery] = useLazyGetTemplateListQuery();
  const [getFileListQuery, fileListResponse] = useLazyGetFileListQuery();

  const pageSizeRef = useRef(String(PAGE_SIZES.Default_List_Size));
  const tableHeaders = [
    {
      headerName: "Name",
      shouldSort: true,
    },
    {
      headerName: "Unique ID",
      shouldSort: true,
    },
    {
      headerName: "Brand Name",
      shouldSort: true,
    },
    {
      headerName: "Family Name",
      shouldSort: true,
    },
    {
      headerName: "Model Name",
      shouldSort: true,
    },
    {
      headerName: "Firmware Version",
      shouldSort: true,
    },
    {
      headerName: "Template Name",
      shouldSort: true,
    },
    {
      headerName: "Status",
    },
    { headerName: "" },
  ];

  const asyncFormSelectProps = [
    {
      label: "Brand",
      name: "brand_name",
      onChange: (val: string | null, label: string) => {
        if (val !== brandId) {
          setBrandId(val);
          setFamilyId(null);
          setModelId(null);
          setTemplateId(null);
          handleFilterChange({
            updatedField: "brand_id",
            persistedSelection: {
              label,
              value: val,
            },
          });
        }
      },
      fetchQuery: getBrandListQuery,
      fetchQueryParams: {
        startKey: "",
        page_size: pageSizeRef.current,
      },
      optionLabelName: "brand_name",
      placeholder: "Select Brand",
      shouldFetch: true,
      persistedOption: persistDropdownSelector["brand_selector"]
        ? {
            label: persistDropdownSelector["brand_selector"]?.label,
            value: persistDropdownSelector["brand_selector"]?.value,
          }
        : null,
    },
    {
      label: "Family",
      name: "family_name",
      onChange: (val: string | null, label: string) => {
        if (val !== familyId) {
          setFamilyId(val);
          setModelId(null);
          setTemplateId(null);
          handleFilterChange({
            updatedField: "family_id",
            persistedSelection: {
              label,
              value: val,
            },
          });
        }
      },
      fetchQuery: getFamilyListQuery,
      fetchQueryParams: {
        startKey: "",
        page_size: pageSizeRef.current,
        brand_id: brandId,
      },
      optionLabelName: "family_name",
      placeholder: "Select Family",
      key: brandId,
      shouldFetch: brandId?.length,
      persistedOption: persistDropdownSelector["family_selector"]
        ? {
            label: persistDropdownSelector["family_selector"]?.label,
            value: persistDropdownSelector["family_selector"]?.value,
          }
        : null,
    },
    {
      label: "Model",
      name: "model_name",
      onChange: (val: string | null, label: string) => {
        if (val !== modelId) {
          setModelId(val);
          setTemplateId(null);
          handleFilterChange({
            updatedField: "model_id",
            persistedSelection: {
              label,
              value: val,
            },
          });
        }
      },
      fetchQuery: getModelListQuery,
      fetchQueryParams: {
        startKey: "",
        page_size: pageSizeRef.current,
        brand_id: brandId,
        family_id: familyId,
      },
      optionLabelName: "model_name",
      placeholder: "Select Model",
      key: familyId,
      shouldFetch: brandId?.length && familyId?.length,
      persistedOption: persistDropdownSelector["model_selector"]
        ? {
            label: persistDropdownSelector["model_selector"]?.label,
            value: persistDropdownSelector["model_selector"]?.value,
          }
        : null,
    },
    {
      label: "Template",
      name: "template_name",
      onChange: (val: string | null, label: string) => {
        if (val !== templateId) {
          setTemplateId(val);
          handleFilterChange({
            updatedField: "template_id",
            persistedSelection: {
              label,
              value: val,
            },
          });
        }
      },
      fetchQuery: getTemplateListQuery,
      fetchQueryParams: {
        startKey: "",
        page_size: pageSizeRef.current,
        brand_id: brandId,
        family_id: familyId,
        model_id: modelId,
      },
      optionLabelName: "template_name",
      placeholder: "Select Template",
      key: modelId,
      shouldFetch: brandId?.length && familyId?.length && modelId?.length,
      persistedOption: persistDropdownSelector["template_selector"]
        ? {
            label: persistDropdownSelector["template_selector"]?.label,
            value: persistDropdownSelector["template_selector"]?.value,
          }
        : null,
    },
  ];

  const handleGetFileList = async () => {
    try {
      const response = await getFileListQuery({
        startKey: "",
        page_size: pageSizeRef.current,
        brand_id: brandId,
        family_id: familyId,
        model_id: modelId,
        template_id: templateId,
      }).unwrap();

      if (response?.status_code === 200) {
        setTableData(response?.data);
      }
    } catch (error: any) {
      console.log(error);
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Fetch File List Error",
      });
    }
  };

  useEffect(() => {
    if (brandId && familyId && modelId && templateId) {
      handleGetFileList();
    } else {
      setTableData([]);
    }
    // eslint-disable-next-line
  }, [brandId, familyId, modelId, templateId]);

  const updatedTableData = (tableData ?? []).map((tdata) => ({
    Name: tdata.file_name,
    "Unique ID": tdata.id,
    "Brand Name": tdata.brand_name,
    "Family Name": tdata?.family_name,
    "Model Name": tdata?.model_name,
    "Firmware Version": tdata?.firmware_version,
    "Template Name": tdata?.template_name,
    Status: [
      tdata.is_active
        ? { text: "Active", color: "#12da85" }
        : { text: "Inactive", color: "#bbbbbb" },
    ],
    actionIcon: (
      <ThreeDotActionItem
        actionItemsMenuPositionStyles={{
          right: "15px",
          left: "unset",
        }}
        actionIconSubItemsList={[
          {
            actionItemName: "View Details",
            onClick: (id: string) =>
              navigate(`/provisioning/file/create?id=${tdata.id}`),
          },
          {
            actionItemName: "Edit Details",
            onClick: (id: string) =>
              navigate(`/provisioning/file/edit?id=${tdata.id}`),
          },
        ]}
        id={tdata.id}
      />
    ),
  }));

  const handleLoadMoreClick = async () => {
    try {
      if (fileListResponse.isSuccess && fileListResponse.data?.next_start_key) {
        const response = await getFileListQuery(
          {
            startKey: fileListResponse.data?.next_start_key ?? "",
            page_size: pageSizeRef.current,
            brand_id: brandId,
            family_id: familyId,
            model_id: modelId,
            template_id: templateId,
          },
          true
        ).unwrap();

        if (response && response?.data?.data) {
          setTableData((prev) => [...prev, ...response?.data?.data]);
        }
      }
    } catch (error: any) {
      console.log(error);
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Load More File List Error",
      });
    }
  };

  return (
    <>
      <Layout>
        <MainHeading headerText="File" subHeaderText={"Description"}>
          <CustomButton
            isSubmitting={false}
            label="Create File"
            customClass="w-11rem px-1 py-0 m-0 h-48 fs-6"
            onClick={() => navigate("create")}
            disabled={false}
          />
        </MainHeading>

        <Grid
          tableHeaders={tableHeaders}
          tableData={updatedTableData}
          isLoading={fileListResponse.isLoading}
          isDataPresent={tableData?.length > 0}
          isError={fileListResponse.isError}
          error={fileListResponse.error}
          noDataUIMainText={CUSTOM_MESSAGES.No_File_Yet}
          noDataUISubText={CUSTOM_MESSAGES.Create_File_Now}
          noDataUIButtonLabel="Create File"
          showPageSizeSelector
          page_size={pageSizeRef.current}
          handlePageSizeChange={(val) => {
            if (val !== pageSizeRef.current) {
              pageSizeRef.current = val as string;
              handleGetFileList();
            }
          }}
          sort={{ sortField: "Name", sortOrder: "asc" }}
          handleClick={() => navigate("create")}
          asyncFormSelectProps={asyncFormSelectProps}
        />

        {((fileListResponse.isSuccess &&
          fileListResponse?.data?.next_start_key) ||
          fileListResponse.isFetching) && (
          <div className="text-center" id="loadmore-button">
            <CustomButton
              isSubmitting={fileListResponse.isFetching}
              label="Load More"
              customClass="px-1 mw-100 py-0 m-0 mt-3 fs-6"
              customStyles={{ width: "30rem" }}
              onClick={handleLoadMoreClick}
            />
          </div>
        )}
      </Layout>
    </>
  );
};

export default FilePortal;
